import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useWorkspaceRoles } from '../../hooks/useWorkspaceRoles';
import { apiRequest } from '../../lib/queryClient';
import { useToast } from '../../hooks/use-toast';

interface RoleSelectorProps {
  memberId: string;
  currentRole: string;
  disabled?: boolean;
}

export default function RoleSelector({ memberId, currentRole, disabled }: RoleSelectorProps) {
  const { currentWorkspace } = useWorkspace();
  const { data: roles = [] } = useWorkspaceRoles();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const updateRoleMutation = useMutation({
    mutationFn: async (roleId: string) => {
      await apiRequest('PATCH', `/api/workspaces/${currentWorkspace?._id}/members/${memberId}/role`, { roleId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast({ title: 'Role updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const currentRoleObj = roles.find((r) => r.name === currentRole);

  return (
    <Select
      value={currentRoleObj?._id || ''}
      onValueChange={(roleId) => updateRoleMutation.mutate(roleId)}
      disabled={disabled || updateRoleMutation.isPending}
    >
      <SelectTrigger className="w-32">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {roles.map((role) => (
          <SelectItem key={role._id} value={role._id}>
            {role.displayName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
